import Vue from 'vue';
import axios from 'axios';
import {getProxyAccessToken} from '@/plugins/cookie-plugins';
import {proxyPath, proxyBody, proxyHeaders} from '@/utils/b2bProxy';

/**
 * betFreeze — the player forces an open leg of a pending multiple to WON at a
 * reduced odd. rocky-bets endpoints:
 *   POST /cart/v3/bet-freeze/preview {code}                      → per-leg quotes
 *   POST /cart/v3/bet-freeze         {code, legUniqueId, quoteId} → execution
 * Every rule is enforced server-side; the UI only renders what the preview
 * says and never computes a price itself.
 */

export const BET_FREEZE_REQUEST_TIMEOUT_MS = 15000;
// Execution may include the live acceptance delay (up to 10 s) plus the
// rocky-definition retries: a wider timeout than the read calls.
export const BET_FREEZE_EXECUTE_TIMEOUT_MS = 30000;

// Bet list teaser: availability of each pending multiple is re-checked at
// most once per this interval.
export const BET_FREEZE_AVAILABILITY_TTL_MS = 60000;
// Bet list teaser: how many pending multiples (most recent first) are checked
// per list load, and the pause between two consecutive requests. The sweep is
// sequential — never more than one availability request in flight.
export const BET_FREEZE_AVAILABILITY_MAX_BETS = 10;
export const BET_FREEZE_AVAILABILITY_DELAY_MS = 300;

// Confirmation panel left open: the quote is re-priced by itself when it
// expires, at most this many times in a row without a player action (and
// never while the tab is hidden) — an idle panel must not poll forever.
export const BET_FREEZE_MAX_AUTO_REPRICES = 5;

/**
 * Quote expiry on THIS device's clock (ms), what the countdown and the
 * confirm check use. The server stamps `expiresAt` with its own clock: a
 * player clock ahead of it by more than the validity saw every quote already
 * expired — a re-price every second while the panel was open, and a Confirm
 * that only ever re-priced. Counted from when the request left, so it never
 * outlives the server-side quote. Falls back to `expiresAt` when the
 * response carries no validity.
 */
export function localQuoteExpiry(leg, requestedAt, validityMs) {
    if (!leg || !leg.quoteId) return null;
    if (validityMs) return requestedAt + validityMs;
    const serverExpiry = Date.parse(leg.expiresAt);
    return Number.isFinite(serverExpiry) ? serverExpiry : null;
}

function endpoint(rootGetters, path) {
    const proxyUrl = rootGetters['settings/getProxyUrl'];
    return {
        url: `${proxyUrl}${proxyPath(rootGetters, path)}`,
        headers: proxyHeaders(rootGetters, getProxyAccessToken()),
    };
}

// Preview entry shape, keyed by bet.code:
// {
//   status: 'loading' | 'ready' | 'unavailable' | 'error'
//   legs: { [legUniqueId]: {freezable, reason?, temporary?, initialOdd, currentOdd?, frozenOdd?,
//                           trend?, quoteId?, expiresAt?, localExpiresAt?, estimatedPotentialWinning?} }
//         (localExpiresAt: quote expiry on this device's clock, see localQuoteExpiry)
//   errors: [{code, message}]     bet-level refusals ('unavailable')
//   maxFreezes, frozenCount, fetchedAt
// }
const state = {
    previews: {},
    // Bet list teaser, keyed by bet.code: {freezable, freezableLegs, checkedAt}
    availability: {},
};

const getters = {
    getBetFreezePreview: (state) => (code) => state.previews[code] || {status: 'idle', legs: {}, errors: []},
    getBetFreezeAvailability: (state) => (code) => state.availability[code] || null,
};

const mutations = {
    SET_PREVIEW(state, {code, data}) {
        Vue.set(state.previews, code, data);
    },
    SET_AVAILABILITY(state, {code, data}) {
        Vue.set(state.availability, code, data);
    },
    CLEAR_PREVIEW(state, code) {
        Vue.delete(state.previews, code);
    },
};

const actions = {
    /**
     * Fetches fresh per-leg quotes. Always pass bet.code (the long code).
     */
    /**
     * `silent`: re-price without the 'loading' state — the current legs stay
     * on screen (no flicker of an open confirmation panel) until the new
     * quotes arrive.
     */
    async previewBetFreeze({commit, rootGetters}, {code, silent = false}) {
        if (!silent) commit('SET_PREVIEW', {code, data: {status: 'loading', legs: {}, errors: []}});
        const {url, headers} = endpoint(rootGetters, '/cart/v3/bet-freeze/preview');
        const requestedAt = Date.now();
        try {
            const {data: body} = await axios.post(url, proxyBody(rootGetters, {code}), {
                headers,
                timeout: BET_FREEZE_REQUEST_TIMEOUT_MS,
            });
            const validityMs = Number(body.quoteValiditySeconds) > 0 ? Number(body.quoteValiditySeconds) * 1000 : null;
            const legs = {};
            for (const leg of body.legs || []) {
                legs[leg.legUniqueId] = {...leg, localExpiresAt: localQuoteExpiry(leg, requestedAt, validityMs)};
            }
            commit('SET_PREVIEW', {
                code,
                data: {
                    status: (body.errors && body.errors.length) ? 'unavailable' : 'ready',
                    legs,
                    errors: body.errors || [],
                    maxFreezes: body.maxFreezes,
                    frozenCount: body.frozenCount,
                    fetchedAt: Date.now(),
                },
            });
            // The detail preview is fresher than the bet list teaser: reuse it
            // for the teaser (no extra request) and restart its TTL.
            const freezableLegs = (body.legs || []).filter(l => l.freezable).length;
            commit('SET_AVAILABILITY', {
                code,
                data: {
                    freezable: !(body.errors && body.errors.length) && freezableLegs > 0,
                    freezableLegs,
                    checkedAt: Date.now(),
                },
            });
        } catch (e) {
            // Availability left untouched: a failed request says nothing new.
            console.error('[betFreeze] preview failed', e);
            commit('SET_PREVIEW', {code, data: {status: 'error', legs: {}, errors: []}});
        }
    },

    /**
     * Light availability check for the bet list teaser: preview with
     * quotes:false (no quote written server-side). Skipped when checked less
     * than BET_FREEZE_AVAILABILITY_TTL_MS ago, unless `force`.
     * Resolves true when a request was actually sent (the caller paces on it).
     */
    async checkBetFreezeAvailability({commit, state, rootGetters}, {code, force = false}) {
        const cached = state.availability[code];
        if (!force && cached && Date.now() - cached.checkedAt < BET_FREEZE_AVAILABILITY_TTL_MS) return false;
        const {url, headers} = endpoint(rootGetters, '/cart/v3/bet-freeze/preview');
        try {
            const {data: body} = await axios.post(url, proxyBody(rootGetters, {code, quotes: false}), {
                headers,
                timeout: BET_FREEZE_REQUEST_TIMEOUT_MS,
            });
            commit('SET_AVAILABILITY', {
                code,
                data: {freezable: body.result === true && (body.freezableLegs || 0) > 0, freezableLegs: body.freezableLegs || 0, checkedAt: Date.now()},
            });
        } catch (e) {
            console.error('[betFreeze] availability check failed', code, e);
            commit('SET_AVAILABILITY', {code, data: {freezable: false, freezableLegs: 0, checkedAt: Date.now()}});
        }
        return true;
    },

    /**
     * Executes a quote. Returns {success, errorCode?, outcomeUnknown?, potentialWinning?}.
     * On failure the caller refreshes the preview (quotes are single use).
     */
    async executeBetFreeze({rootGetters}, {code, legUniqueId, quoteId}) {
        const {url, headers} = endpoint(rootGetters, '/cart/v3/bet-freeze');
        try {
            const {data: body} = await axios.post(url, proxyBody(rootGetters, {code, legUniqueId, quoteId}), {
                headers,
                timeout: BET_FREEZE_EXECUTE_TIMEOUT_MS,
            });
            if (body.betFreezeConfirmed === true) {
                return {success: true, potentialWinning: body.potentialWinning};
            }
            const first = (body.errors && body.errors[0]) || {};
            return {success: false, errorCode: first.code || 'BET_FREEZE_REFUSED', outcomeUnknown: body.outcomeUnknown === true};
        } catch (e) {
            console.error('[betFreeze] execute failed', e);
            // No answer: the freeze may or may not have been applied — the bet
            // list refresh shows the real state.
            return {success: false, errorCode: 'BET_FREEZE_OUTCOME_UNKNOWN', outcomeUnknown: true};
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
